/**
 * d3 grouped column chart — drop-in for the Highcharts path in `../barChart.ts`.
 *
 * Conventions settled during review of the horizontal bar: no gridlines, value
 * labels below the axis rule with real clearance, mono label type.
 */

import { scaleLinear } from "d3-scale";
import { SERIES_COLORS, SERIES_FADED_COLORS, type BarChartPayload } from "../../normalize.js";
import { measureTextWidth } from "../geometry/plotWidth.js";
import { AXIS_COLOR, LABEL_FONT_SIZE, el, labelStyle, makeSvg, makeTooltip , renderResponsive, runAnimation } from "./dom.js";

const CHART_HEIGHT = 320;
const SPACING_TOP = 8;
const SPACING_BOTTOM = 8;
const GROUP_PADDING = 0.22;
const POINT_PADDING = 0.08;
const MAX_POINT_WIDTH = 18;
/**
 * Clear space between the value labels and the left edge of the plot.
 *
 * This is the control for that gap — not PLOT_SHIFT_X. Because labels are drawn at
 * `plotLeft - Y_LABEL_GAP`, shifting the plot moves the labels with it and the gap
 * never changes; raising this value moves only the plot edge, so the chart narrows
 * and the labels stay put.
 */
const Y_LABEL_GAP = 16;
/** Clearance below the plot before the category labels. */
const CATEGORY_LABEL_GAP = 14;
/** Horizontal correction applied on review; labels read slightly left of centre. */
const CATEGORY_LABEL_X_NUDGE = 2;

/** Angle used when horizontal category labels will not fit side by side. */
const CATEGORY_LABEL_ROTATION_DEG = -45;
/**
 * Rotate once the widest label needs more than this share of its band.
 *
 * Raised from 0.9 to 1.15 on UAT: d3 was angling labels at tablet widths where
 * Highcharts still keeps them flat, which shortened the chart and made the two
 * versions differ in height. Highcharts tolerates adjacent labels almost touching, and
 * comparing the *widest* label against every band is already conservative — most bands
 * hold a much shorter label.
 */
const CATEGORY_LABEL_FIT_RATIO = 1.15;

/**
 * Whole-plot offsets set by review: the chart sat a couple of pixels low and a few
 * left of where it should. Applied to the plot origin so bars, both axes and every
 * label move together — nudging them individually would drift out of alignment.
 * The right edge stays put, so shifting right slightly narrows the plot.
 */
const PLOT_SHIFT_X = 6;
const PLOT_SHIFT_Y = -2;

/**
 * Compresses the plot from the top only, leaving the bottom where it is.
 *
 * Review showed the value scale running slightly tall: the bottom tick sat exactly
 * right while the top tick was a few pixels high. That is a plot-height problem, not
 * a position one, so it needs its own control rather than another whole-plot shift.
 */
const PLOT_TOP_INSET = 4;

export interface D3ColumnOptions {
  valueTicks: { min: number; max: number; positions: number[] };
  embedded: boolean;
  esc: (s: string) => string;
}

function drawColumn(
  mount: HTMLElement,
  data: BarChartPayload,
  opts: D3ColumnOptions,
  animateIn: boolean
): () => void {
  const { valueTicks, embedded, esc } = opts;
  const chartWidth = mount.clientWidth;
  const spacingLeft = embedded ? 0 : 20;
  const spacingRight = embedded ? 12 : 20;

  const widestYLabel = valueTicks.positions.reduce(
    (max, v) =>
      Math.max(
        max,
        measureTextWidth(String(v), {
          fontStack: "var(--an26-font-mono), monospace",
          fontSizePx: LABEL_FONT_SIZE,
          fontWeight: "300",
        })
      ),
    0
  );

  const plotLeft = spacingLeft + widestYLabel + Y_LABEL_GAP + PLOT_SHIFT_X;
  const plotWidth = Math.max(0, chartWidth - plotLeft - spacingRight);
  const plotTop = SPACING_TOP + PLOT_SHIFT_Y + PLOT_TOP_INSET;

  const measureCategory = (text: string): number =>
    measureTextWidth(text, {
      fontStack: "var(--an26-font-mono), monospace",
      fontSizePx: LABEL_FONT_SIZE,
      fontWeight: "300",
    });
  const widestCategory = data.periods.reduce((max, c) => Math.max(max, measureCategory(c)), 0);
  const bandWidthGuess = data.periods.length ? plotWidth / data.periods.length : plotWidth;

  // Highcharts rotates category labels rather than letting them collide. Reproduced by
  // measuring: if the widest label needs more than its share of a band, angle them all.
  // Angled labels are taller, so the plot has to give up height for them — which is
  // also what stops the chart looking too tall on narrow viewports.
  const rotateCategories = widestCategory > bandWidthGuess * CATEGORY_LABEL_FIT_RATIO;
  const radians = (Math.abs(CATEGORY_LABEL_ROTATION_DEG) * Math.PI) / 180;
  const categoryGutter = rotateCategories
    ? widestCategory * Math.sin(radians) + LABEL_FONT_SIZE
    : LABEL_FONT_SIZE;

  const plotBottom =
    CHART_HEIGHT - SPACING_BOTTOM - categoryGutter - CATEGORY_LABEL_GAP + PLOT_SHIFT_Y;
  const plotHeight = Math.max(0, plotBottom - plotTop);

  const y = scaleLinear().domain([valueTicks.min, valueTicks.max]).range([plotBottom, plotTop]);

  const svg = makeSvg(chartWidth, CHART_HEIGHT);

  /* value axis labels (left) */
  for (const tick of valueTicks.positions) {
    const label = el("text", { x: plotLeft - Y_LABEL_GAP, y: y(tick) + 4, "text-anchor": "end" });
    labelStyle(label);
    label.textContent = String(tick);
    svg.append(label);
  }

  /* category labels. No axis rule here: Highcharts sets xAxis.lineWidth 0, so the
   * zero plotLine below is the only horizontal rule on this chart. */
  const bandWidth = data.periods.length ? plotWidth / data.periods.length : plotWidth;
  data.periods.forEach((period, pi) => {
    const cx = plotLeft + bandWidth * (pi + 0.5) + CATEGORY_LABEL_X_NUDGE;
    const cy = plotBottom + LABEL_FONT_SIZE + CATEGORY_LABEL_GAP;
    const label = el("text", {
      x: cx,
      y: cy,
      // Angled labels end at the tick and run down-left, so they read upward toward
      // the axis the way the Highcharts version does.
      "text-anchor": rotateCategories ? "end" : "middle",
    });
    if (rotateCategories) {
      label.setAttribute("transform", `rotate(${CATEGORY_LABEL_ROTATION_DEG} ${cx} ${cy})`);
    }
    labelStyle(label);
    label.textContent = period;
    svg.append(label);
  });

  /* Zero reference line. Drawn whenever zero is on the axis at all — not only when
   * the axis dips below it. Highcharts declares this as an unconditional plotLine at
   * value 0, so for all-positive data it lands at the foot of the plot and reads as
   * the bottom axis rule. Gating it on min < 0 left the Calendar Year Returns pane,
   * whose data is entirely positive, with no bottom rule. */
  if (valueTicks.min <= 0 && valueTicks.max >= 0) {
    svg.append(
      el("line", {
        x1: plotLeft,
        y1: y(0),
        x2: plotLeft + plotWidth,
        y2: y(0),
        stroke: AXIS_COLOR,
        "stroke-width": 1,
      })
    );
  }

  /* columns */
  const seriesCount = data.series.length || 1;
  const groupWidth = bandWidth * (1 - 2 * GROUP_PADDING);
  // Each series gets an equal slot; the bar is drawn narrower than its slot and
  // centred in it, which is what opens the gap between adjacent columns. Drawing
  // bars slot-width wide made them touch.
  const slotWidth = groupWidth / seriesCount;
  const barWidth = Math.min(slotWidth * (1 - 2 * POINT_PADDING), MAX_POINT_WIDTH);
  const barsBySeries: SVGRectElement[][] = data.series.map(() => []);
  /** Final geometry per bar, so the entrance animation can interpolate to it. */
  const grown: Array<{ rect: SVGRectElement; top: number; height: number }> = [];
  const zeroY = y(Math.max(0, valueTicks.min));

  data.series.forEach((series, si) => {
    const color = String(SERIES_COLORS[si % SERIES_COLORS.length]);
    data.periods.forEach((_, pi) => {
      const value = series.data[pi];
      if (value == null || !Number.isFinite(value)) return;
      const bandLeft = plotLeft + bandWidth * pi;
      const groupLeft = bandLeft + (bandWidth - groupWidth) / 2;
      const top = Math.min(y(value), zeroY);
      const height = Math.abs(y(value) - zeroY);

      const rect = el("rect", {
        x: groupLeft + slotWidth * si + (slotWidth - barWidth) / 2,
        y: top,
        width: barWidth,
        height,
        fill: color,
      });
      rect.dataset.series = String(si);
      rect.dataset.value = String(value);
      rect.dataset.name = series.name;
      barsBySeries[si]!.push(rect);
      grown.push({ rect, top, height });
      svg.append(rect);
    });
  });

  mount.replaceChildren(svg);

  // Bars grow up out of the zero line, as the Highcharts version does.
  const stopAnimation = animateIn
    ? runAnimation((t) => {
        for (const { rect, top, height } of grown) {
          const h = height * t;
          rect.setAttribute("height", String(h));
          rect.setAttribute("y", String(top < zeroY ? zeroY - h : zeroY));
        }
      })
    : null;

  const tip = makeTooltip(mount);

  const setFade = (active: number | null): void => {
    data.series.forEach((_, si) => {
      const base = String(SERIES_COLORS[si % SERIES_COLORS.length]);
      const faded = String(SERIES_FADED_COLORS[si % SERIES_FADED_COLORS.length]);
      const color = active !== null && si !== active ? faded : base;
      for (const rect of barsBySeries[si]!) rect.setAttribute("fill", color);
    });
  };

  const onOver = (ev: Event): void => {
    const rect = ev.target as SVGRectElement;
    if (!rect.dataset?.series) return;
    setFade(Number(rect.dataset.series));
    const v = Number(rect.dataset.value);
    tip.innerHTML = `<div class="an26-tooltip-hc"><div class="an26-tooltip-hc__line">${esc(
      String(rect.dataset.name)
    )}: <span class="an26-tooltip-hc__val">${esc(v.toFixed(2))}</span></div></div>`;
    tip.style.display = "block";
    tip.style.left = `${Number(rect.getAttribute("x")) + barWidth + 8}px`;
    tip.style.top = `${Number(rect.getAttribute("y")) - 4}px`;
  };
  const onLeave = (): void => {
    setFade(null);
    tip.style.display = "none";
  };

  svg.addEventListener("mouseover", onOver);
  mount.addEventListener("mouseleave", onLeave);

  return () => {
    stopAnimation?.();
    svg.removeEventListener("mouseover", onOver);
    mount.removeEventListener("mouseleave", onLeave);
    mount.replaceChildren();
  };
}

/**
 * Public entry point: draws immediately and redraws on container resize, so the
 * chart stays correct when a device rotates or a layout reflows.
 */
export function renderD3ColumnChart(
  mount: HTMLElement,
  data: BarChartPayload,
  opts: D3ColumnOptions
): () => void {
  return renderResponsive(mount, (animateIn) => drawColumn(mount, data, opts, animateIn));
}
